import { useState } from 'react';
import { SkeletonProfile } from '@/components/skeletons/SkeletonProfile';
import { QueryErrorState } from '@/components/shared/QueryErrorState';
import { toast, confirmDelete } from '@/lib/swal';
import { photoUploadErrorMessage } from '@/components/shared/PhotoUpload';
import type { UpdateMyProfilePayload } from '@/types/profile.types';
import { useMyProfile } from './use-my-profile';
import { IdentitySection } from './sections/IdentitySection';
import { ContactSection } from './sections/ContactSection';
import { EmploymentSection } from './sections/EmploymentSection';
import { EmergencyContactSection } from './sections/EmergencyContactSection';
import {
  AccountSection,
  PreferencesSection,
  SecuritySection,
} from './sections/SecurityAndAccountSections';

/**
 * User Profile P1 — the tenant-staff "My Profile" page.
 *
 * One page for Owner, Admin, Operations User, Commercial User, Service Engineer
 * and Master Service Engineer: sections are the same components everywhere and
 * render from the server's `capabilities` (derived from the field policy), so
 * nothing is offered that `PATCH /auth/me` would refuse, and nothing that the
 * server keeps administrator-only is ever presented as editable. The server
 * remains the authority; this page only mirrors it.
 */
function fieldErrorsOf(error: unknown): Record<string, string> {
  const data = (error as { response?: { data?: unknown } } | undefined)?.response?.data as
    { message?: unknown; error?: { message?: unknown } } | undefined;
  const messages = data?.message ?? data?.error?.message;
  const out: Record<string, string> = {};
  if (Array.isArray(messages)) {
    for (const m of messages) {
      if (typeof m !== 'string') continue;
      const field = m.split(' ')[0] ?? '';
      if (field) out[field] = m;
    }
  }
  return out;
}

export function ProfilePage() {
  const { query, update, uploadPhoto, deletePhoto } = useMyProfile();
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({});

  if (query.isLoading) return <SkeletonProfile rows={8} />;
  if (query.isError || !query.data) {
    return <QueryErrorState subject="your profile" onRetry={() => void query.refetch()} />;
  }
  const me = query.data;

  const save = async (payload: UpdateMyProfilePayload): Promise<boolean> => {
    try {
      await update.mutateAsync(payload);
      setServerErrors({});
      toast.success('Profile updated');
      return true;
    } catch (e) {
      const fields = fieldErrorsOf(e);
      setServerErrors(fields);
      toast.error(
        Object.keys(fields).length > 0
          ? 'Please correct the highlighted fields.'
          : 'Your profile could not be saved. Please try again.',
      );
      return false;
    }
  };

  return (
    <div className="mx-auto max-w-3xl space-y-4 sm:space-y-6" data-testid="profile-page">
      <div>
        <h1 className="text-xl font-semibold text-foreground">My Profile</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          What the service team knows about you, and the details you can keep up to date yourself.
        </p>
      </div>

      <IdentitySection
        me={me}
        uploading={uploadPhoto.isPending || deletePhoto.isPending}
        onPhotoUpload={async (cropped) => {
          // PP-1: `cropped` is the editor's 512 px JPEG, never the picked file.
          try {
            await uploadPhoto.mutateAsync(cropped);
            toast.success('Photo updated');
            return true;
          } catch (e) {
            toast.error(photoUploadErrorMessage(e));
            return false;
          }
        }}
        onPhotoReject={(message) => toast.error(message)}
        onPhotoRemove={() => {
          void (async () => {
            if (await confirmDelete('profile photo')) {
              deletePhoto.mutate(undefined, {
                onSuccess: () => toast.success('Photo removed'),
                onError: () => toast.error('The photo could not be removed. Please try again.'),
              });
            }
          })();
        }}
      />

      <ContactSection me={me} saving={update.isPending} serverErrors={serverErrors} onSave={save} />

      <EmploymentSection me={me} />

      <EmergencyContactSection
        me={me}
        saving={update.isPending}
        serverErrors={serverErrors}
        onSave={save}
      />

      <SecuritySection me={me} />

      <AccountSection me={me} />

      <PreferencesSection />
    </div>
  );
}
